import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import React, { useRef, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { sendContactUsFeedback } from "src/api/sendContactUsFeedback";

const BG = "#EDEDED";
const black = PureLightThemeCaaS.colors.alpha.black[100];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const THANKS_DELAY_MS = 3000;

type ContactStep = "firstName" | "lastName" | "email" | "message" | "image" | "thanks";

const STEP_ORDER: ContactStep[] = ["firstName", "lastName", "email", "message", "image", "thanks"];

interface ContactUsFormFlowProps {
  accessAuthToken: string | null;
  standalone?: boolean;
  onFinished?: () => void;
  /** Typeform hidden field: contact_us | report_bug | suggest_feature */
  section?: string;
}

const pillButtonSx = {
  bgcolor: black,
  color: "#ffffff",
  borderRadius: "999px",
  textTransform: "none" as const,
  fontWeight: 700,
  fontSize: 16,
  px: 3.5,
  py: 1.15,
  mt: 3,
  "&:hover": { bgcolor: black },
  "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.3)", color: "#ffffff" },
};

const underlineFieldSx = {
  maxWidth: 420,
  width: "100%",
  "& .MuiInput-underline:before": { borderBottomColor: black },
  "& .MuiInput-underline:after": { borderBottomColor: black },
  "& .MuiInputBase-input": { color: black, fontSize: 20 },
  "& .MuiInputBase-input::placeholder": { color: "rgba(0,0,0,0.4)", opacity: 1, fontStyle: "italic", fontSize: 20 },
};

const ContactUsFormFlow: React.FC<ContactUsFormFlowProps> = ({
  accessAuthToken,
  standalone = false,
  onFinished,
  section = "contact_us",
}) => {
  const [step, setStep] = useState<ContactStep>("firstName");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [emailError, setEmailError] = useState(false);
  const [imageError, setImageError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stepIndex = STEP_ORDER.indexOf(step);

  const goNext = () => {
    const next = STEP_ORDER[stepIndex + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const prev = STEP_ORDER[stepIndex - 1];
    if (prev && prev !== "thanks") setStep(prev);
  };

  const handleEmailNext = () => {
    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    goNext();
  };

  const handleSubmit = async () => {
    if (!accessAuthToken || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendContactUsFeedback(accessAuthToken, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        message: message.trim(),
        section,
        image,
      });
      setStep("thanks");
      if (!standalone) {
        setTimeout(() => onFinished?.(), THANKS_DELAY_MS);
      }
    } catch {
      setIsSubmitting(false);
    }
  };

  const onPickImage = (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError("Size limit: 10MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setImageError("Please choose an image file");
      return;
    }
    setImageError("");
    setImage(file);
  };

  const renderStepLabel = (n: number, label: string, optional = false) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, width: "100%", maxWidth: 420 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          bgcolor: black,
          color: "#ffffff",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        {n}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 600, color: black, lineHeight: 1.2, fontSize: 24 }}>
          {label}
          {!optional && "*"}
        </Typography>
        {optional && (
          <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: 15 }}>(optional)</Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        bgcolor: BG,
        minHeight: 0,
        width: "100%",
        alignSelf: "stretch",
        position: "relative",
      }}
    >
      {step !== "firstName" && step !== "thanks" && (
        <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 1 }}>
          <Button onClick={goBack} sx={{ minWidth: 0, color: black }}>
            <ArrowBackIcon />
          </Button>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          pb: 4,
          minHeight: 300,
          gap: 2,
          width: "100%",
        }}
      >
        {step === "firstName" && (
          <>
            {renderStepLabel(1, "First Name")}
            <TextField
              fullWidth
              autoFocus
              variant="standard"
              placeholder="Type your answer here..."
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && firstName.trim()) goNext();
              }}
              sx={underlineFieldSx}
            />
            <Button disabled={!firstName.trim()} onClick={goNext} sx={pillButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "lastName" && (
          <>
            {renderStepLabel(2, "Last Name")}
            <TextField
              fullWidth
              autoFocus
              variant="standard"
              placeholder="Type your answer here..."
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && lastName.trim()) goNext();
              }}
              sx={underlineFieldSx}
            />
            <Button disabled={!lastName.trim()} onClick={goNext} sx={pillButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "email" && (
          <>
            {renderStepLabel(3, "Email")}
            <TextField
              fullWidth
              autoFocus
              variant="standard"
              type="email"
              placeholder="Type your answer here..."
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email.trim()) handleEmailNext();
              }}
              sx={underlineFieldSx}
            />
            {emailError && (
              <Box
                sx={{
                  mt: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: "999px",
                  bgcolor: "#fde7e7",
                  border: "1px solid #e5a0a0",
                  color: "#9b3b3b",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
                Hmm... that email doesn't look right
              </Box>
            )}
            <Button disabled={!email.trim()} onClick={handleEmailNext} sx={pillButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "message" && (
          <>
            {renderStepLabel(4, "Message")}
            <TextField
              fullWidth
              autoFocus
              multiline
              minRows={2}
              variant="standard"
              placeholder="Type your answer here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && message.trim()) {
                  e.preventDefault();
                  goNext();
                }
              }}
              sx={underlineFieldSx}
            />
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.7)", fontSize: 14 }}>
              Shift + Enter to make a line break
            </Typography>
            <Button disabled={!message.trim()} onClick={goNext} sx={pillButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "image" && (
          <>
            {renderStepLabel(5, "Upload an image", true)}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => onPickImage(e.target.files?.[0] || null)}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onPickImage(e.dataTransfer.files?.[0] || null);
              }}
              sx={{
                width: "100%",
                maxWidth: 420,
                border: "1px dashed rgba(0,0,0,0.45)",
                borderRadius: "24px",
                py: 4,
                px: 2,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.45)" }} />
              <Typography sx={{ color: black, mt: 1, fontSize: 16 }}>
                {image ? image.name : "Choose file or drag here"}
              </Typography>
              <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: 14 }}>Size limit: 10MB</Typography>
              {imageError && (
                <Typography sx={{ color: "#9b3b3b", fontSize: 14, mt: 0.5 }}>{imageError}</Typography>
              )}
            </Box>
            <Button onClick={() => void handleSubmit()} disabled={isSubmitting || !accessAuthToken} sx={pillButtonSx}>
              {isSubmitting ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Submit"}
            </Button>
          </>
        )}

        {step === "thanks" && (
          <Typography variant="h3" sx={{ textAlign: "center", fontWeight: 600, color: black }}>
            Thanks for reaching out
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ContactUsFormFlow;
